// src/features/cart/cartSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Define and EXPORT the Item type for use in other components
export interface Item {
  id: string;
  name: string;
  price: number;
  quantity: number;
  imageUrl: string;
  category: string;
  color: string;
  size: string;
  colors: string[];
  sizes: string[];
  /** Primary product image (preferred over legacy `image_urls`). */
  primary_image_url?: string;
  /** Legacy multi-image support (kept for backward compatibility). */
  image_urls?: string[];
  product_image_link?: string;
  variant_id?: string;
  hex_code?: string;
}

interface CartState {
  items: Item[];
  total: number;
}

export const cartInitialState: CartState = {
  items: [],
  total: 0,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState: cartInitialState,
  reducers: {
    addToCart: (state, action: PayloadAction<Item>) => {
      const existingItem = state.items.find((item) => item.id === action.payload.id);
      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        state.items.push(action.payload);
      }
      state.total += action.payload.price;
    },
    removeFromCart: (state, action: PayloadAction<string>) => {
      const index = state.items.findIndex((item) => item.id === action.payload);
      if (index !== -1) {
        const item = state.items[index];
        state.total -= item.price * item.quantity;
        state.items.splice(index, 1);
      }
    },
    clearCart: (state) => {
      state.items = [];
      state.total = 0;
    },
    // New reducer to replace items by category
    setItemForCategory: (state, action: PayloadAction<Item>) => {
        const newItem = action.payload;
        const categoryToReplace = newItem.category;

        // Recalculate total by first removing price of any existing items in the same category
        const existingItemsInCategory = state.items.filter(item => item.category === categoryToReplace);
        existingItemsInCategory.forEach(item => {
            state.total -= item.price * item.quantity;
        });

        // Filter out the old items from the category
        state.items = state.items.filter(item => item.category !== categoryToReplace);

        // Add the new item
        state.items.push(newItem);
        state.total += newItem.price * newItem.quantity;
    },
  },
});

export const { addToCart, removeFromCart, clearCart, setItemForCategory } = cartSlice.actions;
export default cartSlice.reducer;